/**
 * TasksPage.tsx — Full task management page for Spec 017.
 *
 * Route: /tasks
 *
 * Renders the task list from the Redux taskThreads slice (no new REST fetch if
 * slice is already loaded — FR-014 AS2). Provides filter, search, and a detail
 * pane that renders the 4-layer anatomy (Principle I).
 *
 * Bulk actions issue N REST PATCH calls per OQ-6 resolution.
 *
 * Spec 017 FR-014, FR-016.
 */

import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { tasksPath } from '@logic'
import { taskThreadsActions } from '@logic/reducers/task-threads-reducer'
import { useTaskThreadSubscription } from '@src/modules/task-threads/hooks/useTaskThreadSubscription'
import { getIssue, patchIssueStatus } from '@src/service/services/task-threads/task-thread-client'
import type { RootState } from '@logic/types/types'
import type { TaskThreadItem, TaskStatus, TaskPriority } from '@logic/types/task-thread'

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

function selectTaskThreadsState(state: RootState) {
  return (state as any).taskThreads ?? null
}

function selectTaskThreadCompanyId(state: RootState): string | null {
  return state.organization?.currentOrganization?.id ?? null
}

function selectCurrentUserId(state: RootState): string | null {
  return state.user?.id ?? null
}

// ---------------------------------------------------------------------------
// Filter controls
// ---------------------------------------------------------------------------

type FilterValue = 'all' | 'mine' | 'active' | 'archived'

const FILTER_LABELS: Record<FilterValue, string> = {
  all: 'All',
  mine: 'Assigned to me',
  active: 'Active',
  archived: 'Archived',
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
  cancelled: 'Cancelled',
  archived: 'Archived',
}

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: '#6b7280',
  medium: '#d97706',
  high: '#ea580c',
  urgent: '#dc2626',
}

// ---------------------------------------------------------------------------
// TasksPage
// ---------------------------------------------------------------------------

export const TasksPage: React.FC<RouteComponentProps<{ id?: string }>> = ({ history, match }) => {
  const dispatch = useDispatch()
  const ttState = useSelector(selectTaskThreadsState)
  const companyId = useSelector(selectTaskThreadCompanyId)
  const currentUserId = useSelector(selectCurrentUserId)
  const companyIdForRequest = ttState?.companyId ?? companyId
  const routeTaskId = match.params.id ?? null

  useTaskThreadSubscription({
    companyId,
    surfaceIdentity: 'equa-web-tasks-page',
    enabled: Boolean(companyId),
  })

  const [filter, setFilter] = React.useState<FilterValue>('all')
  const [search, setSearch] = React.useState('')
  const [selectedId, setSelectedId] = React.useState<string | null>(routeTaskId)
  const [bulkSelected, setBulkSelected] = React.useState<Set<string>>(new Set())
  const [bulkActing, setBulkActing] = React.useState(false)
  const [detailLoading, setDetailLoading] = React.useState(false)
  const [detailError, setDetailError] = React.useState<string | null>(null)

  React.useEffect(() => {
    setSelectedId(routeTaskId)
  }, [routeTaskId])

  React.useEffect(() => {
    if (!selectedId || !companyIdForRequest) {
      setDetailLoading(false)
      setDetailError(null)
      return
    }

    let cancelled = false
    setDetailLoading(true)
    setDetailError(null)

    void getIssue(companyIdForRequest, selectedId)
      .then(issue => {
        if (!cancelled) {
          dispatch(taskThreadsActions.hydrateOne(issue))
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error('[TasksPage] detail load failed:', err)
          setDetailError(String(err))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [companyIdForRequest, dispatch, selectedId])

  if (!ttState) {
    return (
      <div style={styles.loadingWrap}>
        <span>Initializing task threads…</span>
      </div>
    )
  }

  const { items, order, status } = ttState

  // Apply filters
  const candidateIds = filter === 'archived' ? Object.keys(items) : (order as string[])
  const visibleIds: string[] = candidateIds.filter((id: string) => {
    const item: TaskThreadItem = items[id]
    if (!item) return false
    const matchesSearch =
      !search ||
      item.title.toLowerCase().includes(search.toLowerCase())
    if (!matchesSearch) return false
    if (filter === 'archived') {
      return item.status === 'archived'
    }
    if (filter === 'active') {
      return item.status !== 'archived' && item.status !== 'completed' && item.status !== 'cancelled'
    }
    if (filter === 'mine') {
      return Boolean(currentUserId) && item.assigneeId === currentUserId
    }
    return true
  })

  const selectedTask: TaskThreadItem | null = selectedId ? items[selectedId] ?? null : null

  const navigateToTask = (taskId: string | null) => {
    setSelectedId(taskId)
    history.push(taskId ? `${tasksPath}/${encodeURIComponent(taskId)}` : tasksPath)
  }

  // Bulk archive: N REST calls per OQ-6
  const handleBulkArchive = async () => {
    if (bulkSelected.size === 0 || bulkActing || !companyIdForRequest) return
    setBulkActing(true)
    try {
      for (const taskId of bulkSelected) {
        const previousStatus = items[taskId]?.status
        try {
          dispatch(taskThreadsActions.optimisticUpdate(taskId, { status: 'archived' }))
          const updated = await patchIssueStatus(companyIdForRequest, taskId, 'archived', ttState.worktreeId)
          dispatch(taskThreadsActions.hydrateOne(updated))
        } catch (err) {
          if (previousStatus) {
            dispatch(taskThreadsActions.optimisticUpdate(taskId, { status: previousStatus }))
          }
          console.error('[TasksPage] bulk archive failed for', taskId, err)
        }
      }
    } finally {
      setBulkSelected(new Set())
      setBulkActing(false)
    }
  }

  const toggleBulk = (id: string) => {
    setBulkSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.heading}>Tasks</h1>
        <div style={styles.syncStatus}>
          <span
            style={{
              ...styles.syncDot,
              backgroundColor:
                status === 'live' ? '#22c55e' : status === 'offline' ? '#ef4444' : '#f59e0b',
            }}
          />
          <span style={styles.syncLabel}>
            {status === 'live' ? 'Live' : status === 'reconnecting' ? 'Reconnecting…' : status}
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        {/* Filter tabs */}
        <div style={styles.filterTabs}>
          {(Object.keys(FILTER_LABELS) as FilterValue[]).map(f => (
            <button
              key={f}
              style={{
                ...styles.filterTab,
                ...(filter === f ? styles.filterTabActive : {}),
              }}
              onClick={() => setFilter(f)}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
        {/* Search */}
        <input
          type="text"
          placeholder="Search tasks…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        {/* Bulk actions */}
        {bulkSelected.size > 0 && (
          <button
            onClick={handleBulkArchive}
            disabled={bulkActing}
            style={styles.bulkButton}
          >
            {bulkActing ? 'Archiving…' : `Archive ${bulkSelected.size} task${bulkSelected.size !== 1 ? 's' : ''}`}
          </button>
        )}
      </div>

      {/* Main layout */}
      <div style={styles.layout}>
        {/* Task list */}
        <div style={styles.listPane}>
          {status === 'loading' && (
            <div style={styles.loadingRow}>Loading tasks…</div>
          )}
          {visibleIds.length === 0 && status !== 'loading' && (
            <div style={styles.emptyState}>No tasks match the current filter.</div>
          )}
          {visibleIds.map((id: string) => {
            const item: TaskThreadItem = items[id]
            return (
              <TaskRow
                key={id}
                item={item}
                isSelected={selectedId === id}
                isBulkSelected={bulkSelected.has(id)}
                onSelect={() => navigateToTask(id === selectedId ? null : id)}
                onBulkToggle={() => toggleBulk(id)}
              />
            )
          })}
        </div>

        {/* Detail pane */}
        {selectedTask && (
          <TaskDetailPane
            task={selectedTask}
            loading={detailLoading}
            error={detailError}
            onClose={() => navigateToTask(null)}
            onStatusChange={async (newStatus: TaskStatus) => {
              if (!companyIdForRequest) return
              const previousStatus = selectedTask.status
              dispatch(taskThreadsActions.optimisticUpdate(selectedTask.id, { status: newStatus }))
              try {
                const updated = await patchIssueStatus(
                  companyIdForRequest,
                  selectedTask.id,
                  newStatus,
                  ttState.worktreeId
                )
                dispatch(taskThreadsActions.hydrateOne(updated))
              } catch (err) {
                dispatch(taskThreadsActions.optimisticUpdate(selectedTask.id, { status: previousStatus }))
                console.error('[TasksPage] status change failed:', err)
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// TaskRow
// ---------------------------------------------------------------------------

interface TaskRowProps {
  item: TaskThreadItem
  isSelected: boolean
  isBulkSelected: boolean
  onSelect: () => void
  onBulkToggle: () => void
}

const TaskRow: React.FC<TaskRowProps> = ({
  item,
  isSelected,
  isBulkSelected,
  onSelect,
  onBulkToggle,
}) => (
  <div
    style={{
      ...styles.taskRow,
      ...(isSelected ? styles.taskRowSelected : {}),
    }}
  >
    <input
      type="checkbox"
      checked={isBulkSelected}
      onChange={onBulkToggle}
      onClick={e => e.stopPropagation()}
      style={styles.checkbox}
    />
    <div style={styles.taskRowContent} onClick={onSelect}>
      <span
        style={{
          ...styles.priorityDot,
          backgroundColor: PRIORITY_COLORS[item.priority],
        }}
      />
      <span style={styles.taskTitle}>{item.title}</span>
      <span style={styles.taskStatus}>{STATUS_LABELS[item.status]}</span>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// TaskDetailPane — 4-layer anatomy (Principle I)
// ---------------------------------------------------------------------------

interface TaskDetailPaneProps {
  task: TaskThreadItem
  loading: boolean
  error: string | null
  onClose: () => void
  onStatusChange: (newStatus: TaskStatus) => void
}

const TaskDetailPane: React.FC<TaskDetailPaneProps> = ({
  task,
  loading,
  error,
  onClose,
  onStatusChange,
}) => {
  const summary = task.summary ?? task.description
  const resources = task.resources ?? []
  const conversation = task.conversation ?? []

  return (
    <div style={styles.detailPane}>
      <div style={styles.detailHeader}>
        <h2 style={styles.detailTitle}>{task.title}</h2>
        <button
          type="button"
          aria-label="Close task details"
          onClick={onClose}
          style={styles.closeButton}
        >
          ✕
        </button>
      </div>

      {loading && <div style={styles.detailNotice}>Loading task details…</div>}
      {error && <div style={styles.detailError}>{error}</div>}

      {/* Layer 1 — Header */}
      <section style={styles.detailSection}>
        <h3 style={styles.sectionHeading}>Header</h3>
        <dl style={styles.dl}>
          <dt>Status</dt>
          <dd>
            <select
              value={task.status}
              onChange={e => onStatusChange(e.target.value as TaskStatus)}
              style={styles.statusSelect}
            >
              {(Object.keys(STATUS_LABELS) as TaskStatus[]).map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </dd>
          <dt>Priority</dt>
          <dd style={{ color: PRIORITY_COLORS[task.priority] }}>
            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
          </dd>
          <dt>Assignee</dt>
          <dd>{task.assigneeId ?? 'Unassigned'}</dd>
          <dt>Updated</dt>
          <dd>{new Date(task.updatedAt).toLocaleString()}</dd>
        </dl>
      </section>

      {/* Layer 2 — Summary */}
      <section style={styles.detailSection}>
        <h3 style={styles.sectionHeading}>Summary</h3>
        <p style={summary ? styles.detailText : styles.placeholderText}>
          {summary || 'No summary available.'}
        </p>
      </section>

      {/* Layer 3 — Resources */}
      <section style={styles.detailSection}>
        <h3 style={styles.sectionHeading}>Resources</h3>
        {resources.length > 0 ? (
          <ul style={styles.resourceList}>
            {resources.map((resource, index) => (
              <li key={`${resource.type}:${resource.label}:${index}`} style={styles.resourceItem}>
                {resource.url ? (
                  <a href={resource.url} target="_blank" rel="noreferrer" style={styles.resourceLink}>
                    {resource.label}
                  </a>
                ) : (
                  <span>{resource.label}</span>
                )}
                {resource.externalSystem && (
                  <span style={styles.resourceMeta}>
                    {resource.externalSystem}{resource.externalId ? `:${resource.externalId}` : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p style={styles.placeholderText}>No resources linked.</p>
        )}
      </section>

      {/* Layer 4 — Conversation */}
      <section style={styles.detailSection}>
        <h3 style={styles.sectionHeading}>Conversation</h3>
        {conversation.length > 0 ? (
          <ul style={styles.messageList}>
            {conversation.map(message => (
              <li key={message.id} style={styles.messageItem}>
                <div style={styles.messageMeta}>
                  {message.authorType} {message.authorId} · {new Date(message.createdAt).toLocaleString()}
                </div>
                <p style={styles.detailText}>{message.body}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p style={styles.placeholderText}>No conversation yet.</p>
        )}
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline styles (no styled-components to keep dependency footprint minimal)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    fontFamily: 'inherit',
    color: '#111827',
    background: '#f9fafb',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 24px 8px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fff',
  },
  heading: { margin: 0, fontSize: 20, fontWeight: 600, flex: 1 },
  syncStatus: { display: 'flex', alignItems: 'center', gap: 6 },
  syncDot: { width: 8, height: 8, borderRadius: '50%' },
  syncLabel: { fontSize: 12, color: '#6b7280' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 24px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fff',
  },
  filterTabs: { display: 'flex', gap: 4 },
  filterTab: {
    padding: '4px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    cursor: 'pointer',
    background: '#fff',
    fontSize: 13,
  },
  filterTabActive: {
    background: '#111827',
    color: '#fff',
    borderColor: '#111827',
  },
  searchInput: {
    flex: 1,
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 13,
    outline: 'none',
  },
  bulkButton: {
    padding: '6px 14px',
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
  },
  layout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  listPane: {
    flex: 1,
    overflow: 'auto',
    padding: '0 0 24px',
  },
  loadingRow: {
    padding: '32px 24px',
    color: '#6b7280',
    textAlign: 'center',
  },
  emptyState: {
    padding: '48px 24px',
    color: '#9ca3af',
    textAlign: 'center',
  },
  taskRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 24px',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    background: '#fff',
    transition: 'background 0.1s',
  },
  taskRowSelected: {
    background: '#f0f9ff',
    borderLeft: '3px solid #0ea5e9',
  },
  checkbox: { marginRight: 12, cursor: 'pointer' },
  taskRowContent: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: 10,
  },
  priorityDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  taskTitle: { flex: 1, fontSize: 14, color: '#111827' },
  taskStatus: {
    fontSize: 12,
    color: '#6b7280',
    background: '#f3f4f6',
    padding: '2px 8px',
    borderRadius: 10,
  },
  detailPane: {
    width: 380,
    flexShrink: 0,
    borderLeft: '1px solid #e5e7eb',
    background: '#fff',
    overflow: 'auto',
    padding: 0,
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
  },
  detailTitle: { flex: 1, margin: 0, fontSize: 16, fontWeight: 600 },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 16,
    color: '#9ca3af',
    padding: 0,
    lineHeight: 1,
  },
  detailSection: {
    padding: '16px 20px',
    borderBottom: '1px solid #f3f4f6',
  },
  detailNotice: {
    padding: '10px 20px',
    borderBottom: '1px solid #dbeafe',
    background: '#eff6ff',
    color: '#1d4ed8',
    fontSize: 12,
  },
  detailError: {
    padding: '10px 20px',
    borderBottom: '1px solid #fecaca',
    background: '#fef2f2',
    color: '#b91c1c',
    fontSize: 12,
  },
  sectionHeading: {
    margin: '0 0 10px',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#6b7280',
  },
  dl: {
    display: 'grid',
    gridTemplateColumns: '80px 1fr',
    gap: '6px 12px',
    margin: 0,
    fontSize: 13,
  },
  placeholderText: { margin: 0, fontSize: 13, color: '#9ca3af' },
  detailText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.5,
    color: '#374151',
    whiteSpace: 'pre-wrap',
  },
  resourceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    margin: 0,
    padding: 0,
    listStyle: 'none',
    fontSize: 13,
  },
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minWidth: 0,
  },
  resourceLink: {
    color: '#0369a1',
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  resourceMeta: {
    flexShrink: 0,
    color: '#6b7280',
    fontSize: 11,
  },
  messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  messageItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  messageMeta: {
    color: '#6b7280',
    fontSize: 11,
  },
  statusSelect: {
    fontSize: 13,
    border: '1px solid #d1d5db',
    borderRadius: 4,
    padding: '2px 6px',
    cursor: 'pointer',
  },
  loadingWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#6b7280',
    fontSize: 14,
  },
}

export default TasksPage
